import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Add,
  ArrowBack,
  CheckCircle,
  FullscreenExit,
  Fullscreen,
  PlayArrow,
  Refresh,
  Remove,
  Stop,
  SwapVert,
  Timeline,
} from "@material-ui/icons";

import {
  formatTime,
  getElapsedMinutes,
  getTeamName,
  hasValue,
  isSessionLive,
} from "../teamPhotos/sessionUtils";
import {
  ZOOM_STEPS,
  loadAutoScroll,
  loadZoom,
  saveAutoScroll,
  saveZoom,
  stepZoom,
} from "./displayPrefs";

// Le temps de jeu se calcule à partir de `created_at` : il avance donc tout
// seul, sans que le serveur ait quoi que ce soit à renvoyer. On rafraîchit
// l'horloge indépendamment du polling pour que l'affichage ne fasse pas des
// bonds d'une minute toutes les 15 secondes.
const CLOCK_TICK_MS = 10000;

// Défilement automatique : assez lent pour qu'une ligne reste lisible en
// passant, avec un retour plus rapide en haut et une pause à chaque extrémité
// pour laisser voir le début et la fin du classement.
const SCROLL_DOWN_PX_PER_S = 60;
const SCROLL_UP_PX_PER_S = 160;
const SCROLL_EDGE_PAUSE_MS = 2600;

// Lueur orangée sous la ligne de tête : elle respire lentement — elle enfle,
// redescend, et son opacité varie d'autant. Purement décoratif, donc masqué aux
// lecteurs d'écran et insensible aux clics. Toute l'animation est en CSS,
// aucune valeur n'est calculée au render.
const LeaderGlow = ({ classes }) => (
  <span className={classes.leaderGlow} aria-hidden="true" />
);

// Classement du direct : le meilleur score en tête, les sessions sans score
// remonté en fin de liste, départage par ordre d'arrivée.
const sortForLeaderboard = (a, b) => {
  const aPoints = hasValue(a.pointsWon) ? a.pointsWon : -1;
  const bPoints = hasValue(b.pointsWon) ? b.pointsWon : -1;
  if (aPoints !== bPoints) return bPoints - aPoints;
  return new Date(a.created_at) - new Date(b.created_at);
};

const StatusChip = ({ session, classes, t }) => {
  const live = isSessionLive(session);

  return (
    <Chip
      size="small"
      label={
        <span className={classes.statusChipLabel}>
          {live ? (
            <span className={classes.liveDot} />
          ) : (
            <CheckCircle className={classes.statusChipIcon} />
          )}
          {live ? t("liveScores.status.live") : t("liveScores.status.ended")}
        </span>
      }
      className={`${classes.statusChip} ${
        live ? classes.statusChipLive : classes.statusChipDone
      }`}
    />
  );
};

// Une session = une ligne. Tout tient sur une seule rangée pour maximiser le
// nombre d'équipes visibles à l'écran : c'est ce qui compte quand la page est
// projetée devant un groupe. Le nom d'équipe est le seul élément élastique, les
// autres colonnes gardent une largeur stable pour que les lignes s'alignent.
const SessionCard = ({ session, rank, classes, t, nowMs }) => {
  const live = isSessionLive(session);
  const total = session.totalPoints;
  const elapsedMinutes = getElapsedMinutes(session, nowMs);
  // Personne n'est « en tête » à zéro point : le premier d'un classement encore
  // vide ne mérite pas les flammes, ce serait trompeur.
  const isLeader = rank === 1 && hasValue(session.pointsWon) && session.pointsWon > 0;

  return (
    <div
      className={`${classes.card} ${live ? classes.cardLive : ""} ${
        isLeader ? classes.cardLeader : ""
      }`}
    >
      {isLeader && <LeaderGlow classes={classes} />}

      {/* Le rang suit la même condition que les flammes : un premier à zéro
          point ne doit pas porter les couleurs du feu sans le feu. */}
      <div className={`${classes.rank} ${isLeader ? classes.rankLeader : ""}`}>
        {rank}
      </div>

      <div className={classes.teamRow}>
        <Typography component="span" className={classes.teamName}>
          {getTeamName(session, t)}
        </Typography>
        <StatusChip session={session} classes={classes} t={t} />
      </div>

      <Typography component="span" className={classes.cardMeta}>
        {t("liveScores.card.zones")} {session.zonesSucceded ?? 0}
        {session.totalZones ? `/${session.totalZones}` : ""}
        {" · "}
        {t("liveScores.card.enigmas")} {session.enigmasSucceded ?? 0}
        {session.totalEnigmas ? `/${session.totalEnigmas}` : ""}
        {hasValue(elapsedMinutes)
          ? ` · ${elapsedMinutes} ${t("liveScores.card.minutes")}`
          : ""}
      </Typography>

      <Typography component="span" className={classes.scoreBlock}>
        <span className={classes.scoreValue}>
          {hasValue(session.pointsWon) ? session.pointsWon : "—"}
          {total ? <span className={classes.scoreTotal}>/{total}</span> : null}
        </span>
        <span className={classes.scoreLabel}>
          {t("liveScores.card.points")}
        </span>
      </Typography>
    </div>
  );
};

// Réglages d'affichage, montés à la fois dans la barre animateur et dans le
// bandeau plein écran : c'est justement projeté qu'on a besoin d'ajuster.
const DisplayControls = ({
  classes,
  t,
  zoom,
  autoScroll,
  onZoomIn,
  onZoomOut,
  onToggleAutoScroll,
}) => (
  <div className={classes.displayControls}>
    <div className={classes.zoomGroup}>
      <Tooltip title={t("liveScores.actions.zoom_out")}>
        <IconButton
          size="small"
          className={classes.zoomButton}
          onClick={onZoomOut}
          disabled={zoom <= ZOOM_STEPS[0]}
          aria-label={t("liveScores.actions.zoom_out")}
        >
          <Remove fontSize="small" />
        </IconButton>
      </Tooltip>
      <span className={classes.zoomValue}>{Math.round(zoom * 100)}%</span>
      <Tooltip title={t("liveScores.actions.zoom_in")}>
        <IconButton
          size="small"
          className={classes.zoomButton}
          onClick={onZoomIn}
          disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
          aria-label={t("liveScores.actions.zoom_in")}
        >
          <Add fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>

    <Tooltip title={t("liveScores.actions.auto_scroll_hint")}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SwapVert />}
        aria-pressed={autoScroll}
        className={`${classes.autoScrollButton} ${
          autoScroll ? classes.autoScrollButtonActive : ""
        }`}
        onClick={onToggleAutoScroll}
      >
        {t("liveScores.actions.auto_scroll")}
      </Button>
    </Tooltip>
  </div>
);

const LiveScoresView = ({
  state,
  classes,
  pollIntervalMs,
  onStartRun,
  onCloseRun,
  onDiscardRun,
  onRefreshNow,
}) => {
  const { t } = useTranslation();
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [zoom, setZoom] = useState(loadZoom);
  const [autoScroll, setAutoScroll] = useState(loadAutoScroll);
  const pageRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const changeZoom = (direction) => {
    const next = stepZoom(zoom, direction);
    setZoom(next);
    saveZoom(next);
  };

  const toggleAutoScroll = () => {
    setAutoScroll((previous) => {
      saveAutoScroll(!previous);
      return !previous;
    });
  };

  // Le conteneur qui défile n'est pas le même selon le mode : en plein écran
  // c'est l'élément projeté (`overflowY: auto`), sinon c'est la fenêtre.
  const getScroller = () => {
    if (document.fullscreenElement === pageRef.current && pageRef.current) {
      const element = pageRef.current;
      return {
        get: () => element.scrollTop,
        set: (value) => {
          element.scrollTop = value;
        },
        max: () => element.scrollHeight - element.clientHeight,
      };
    }

    const doc = document.documentElement;
    return {
      get: () => window.scrollY,
      set: (value) => window.scrollTo(0, value),
      max: () => doc.scrollHeight - window.innerHeight,
    };
  };

  // Va-et-vient continu : descente lente jusqu'en bas, pause, remontée rapide,
  // pause, et on recommence. La position est suivie en flottant à part, parce
  // que `scrollTop` arrondit et qu'à 26 px/s les incréments valent moins d'un
  // pixel par frame — sans accumulateur, le défilement resterait immobile.
  useEffect(() => {
    if (!autoScroll) return undefined;

    let rafId = null;
    let lastFrameMs = performance.now();
    let pausedUntilMs = 0;
    let direction = 1;
    let position = getScroller().get();

    const step = (frameMs) => {
      const deltaMs = frameMs - lastFrameMs;
      lastFrameMs = frameMs;

      const scroller = getScroller();
      const max = scroller.max();

      if (max > 1 && frameMs >= pausedUntilMs) {
        // Une remise à zéro du classement ou un changement de zoom peut avoir
        // déplacé le conteneur sous nos pieds : on se resynchronise.
        if (Math.abs(position - scroller.get()) > 4) position = scroller.get();

        const speed = direction > 0 ? SCROLL_DOWN_PX_PER_S : -SCROLL_UP_PX_PER_S;
        position += (speed * deltaMs) / 1000;

        if (position >= max) {
          position = max;
          direction = -1;
          pausedUntilMs = frameMs + SCROLL_EDGE_PAUSE_MS;
        } else if (position <= 0) {
          position = 0;
          direction = 1;
          pausedUntilMs = frameMs + SCROLL_EDGE_PAUSE_MS;
        }

        scroller.set(Math.round(position));
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [autoScroll, isFullscreen]);

  // Reprendre la main à la molette ou au doigt coupe le défilement : se battre
  // contre l'utilisateur serait pire que de ne rien automatiser. Nos propres
  // écritures de `scrollTop` ne déclenchent pas ces évènements, donc pas
  // d'auto-annulation.
  useEffect(() => {
    if (!autoScroll) return undefined;

    const stop = () => {
      setAutoScroll(false);
      saveAutoScroll(false);
    };

    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchmove", stop, { passive: true });
    return () => {
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchmove", stop);
    };
  }, [autoScroll]);

  // Le plein écran se quitte aussi à la touche Échap, sans passer par notre
  // bouton : on suit l'état réel du document plutôt que nos propres clics.
  useEffect(() => {
    const syncFullscreen = () =>
      setIsFullscreen(document.fullscreenElement === pageRef.current);

    document.addEventListener("fullscreenchange", syncFullscreen);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  const { run, sessions } = state;

  const trackedIds = new Set(run?.sessionIds || []);
  const trackedSessions = sessions
    .filter((session) => trackedIds.has(session.id))
    .sort(sortForLeaderboard);
  const liveCount = trackedSessions.filter(isSessionLive).length;

  const enterFullscreen = () => {
    if (pageRef.current?.requestFullscreen) {
      pageRef.current.requestFullscreen().catch(() => {
        /* refusé par le navigateur : on reste en affichage normal */
      });
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };

  const handleStartRun = () => {
    // Repartir de zéro efface le tableau : on ne confirme que s'il y a
    // effectivement quelque chose à perdre.
    if (run && run.sessionIds.length > 0) {
      setConfirmRestart(true);
      return;
    }
    onStartRun();
  };

  const board = !run ? (
    <div className={classes.empty}>
      <Timeline className={classes.emptyIcon} />
      <Typography variant="h6" className={classes.emptyTitle}>
        {t("liveScores.empty.no_run_title")}
      </Typography>
      <Typography className={classes.emptyText}>
        {t("liveScores.empty.no_run_text")}
      </Typography>
      {/* Le bouton n'a de sens que côté animateur : en plein écran les
          joueurs ne peuvent rien lancer. */}
      {!isFullscreen && (
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          className={classes.primaryButton}
          onClick={onStartRun}
        >
          {t("liveScores.actions.start_run")}
        </Button>
      )}
    </div>
  ) : trackedSessions.length === 0 ? (
    <div className={classes.empty}>
      <Timeline className={classes.emptyIcon} />
      <Typography variant="h6" className={classes.emptyTitle}>
        {t("liveScores.empty.waiting_title")}
      </Typography>
      <Typography className={classes.emptyText}>
        {t("liveScores.empty.waiting_text")}
      </Typography>
      {!isFullscreen && (
        <Button
          variant="outlined"
          className={classes.secondaryButton}
          onClick={onDiscardRun}
        >
          {t("liveScores.actions.discard_run")}
        </Button>
      )}
    </div>
  ) : (
    trackedSessions.map((session, index) => (
      <SessionCard
        key={session.id}
        session={session}
        rank={index + 1}
        classes={classes}
        t={t}
        nowMs={nowMs}
      />
    ))
  );

  if (state.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const displayControls = (
    <DisplayControls
      classes={classes}
      t={t}
      zoom={zoom}
      autoScroll={autoScroll}
      onZoomIn={() => changeZoom(1)}
      onZoomOut={() => changeZoom(-1)}
      onToggleAutoScroll={toggleAutoScroll}
    />
  );

  return (
    <>
      <div
        ref={pageRef}
        className={`${classes.page} ${isFullscreen ? classes.pageStage : ""}`}
      >
        {/* Le zoom est porté par une couche interne et non par la racine : la
            racine garde ainsi son fond et sa hauteur plein écran, sinon un
            zoom inférieur à 100 % laisserait une bande vide sous le contenu.
            Tout le contenu est dedans, pour coller au comportement de
            cmd + / cmd −. */}
        <div className={classes.zoomLayer} style={{ zoom }}>
        {isFullscreen ? (
          // Affichage joueurs : le parcours, le classement, et rien d'autre.
          <div className={classes.stageHeader}>
            <div className={classes.stageBrand}>
              <img
                src="/logo192.png"
                alt="Landing Zone"
                className={classes.stageLogo}
              />
              <div>
                <Typography component="h1" className={classes.stageTourTitle}>
                  {state.tour?.title || t("liveScores.title")}
                </Typography>
                <Typography className={classes.stageSubtitle}>
                  {t("liveScores.stage.subtitle")}
                </Typography>
              </div>
            </div>

            <div className={classes.stageStatus}>
              {liveCount > 0 && (
                <>
                  <span className={classes.liveDot} />
                  {t("liveScores.run.live", { count: liveCount })}
                  <span style={{marginRight: "10px"}} />
                </>
              )}
              {displayControls}
              <IconButton
                className={classes.exitButton}
                onClick={exitFullscreen}
                title={t("liveScores.actions.exit_fullscreen")}
              >
                <FullscreenExit />
              </IconButton>
            </div>
          </div>
        ) : (
          <>
            <div className={classes.header}>
              <div>
                <Link to="/" className={classes.backLink}>
                  <ArrowBack className={classes.backIcon} />
                  {t("liveScores.actions.back")}
                </Link>
                <Typography variant="h4" className={classes.title}>
                  {t("liveScores.title")}
                </Typography>
                <Typography className={classes.subtitle}>
                  {t("liveScores.subtitle")}
                </Typography>
                {state.tour && (
                  <Typography className={classes.tourTitle}>
                    {state.tour.title}
                  </Typography>
                )}
              </div>

              <Button
                variant="contained"
                startIcon={<Fullscreen />}
                className={classes.primaryButton}
                onClick={enterFullscreen}
              >
                {t("liveScores.actions.fullscreen")}
              </Button>
            </div>

            {state.errorMessage && (
              <Box mb={2}>
                <Alert severity="error">{state.errorMessage}</Alert>
              </Box>
            )}

            {/* Sans parcours identifié, aucun enregistrement n'a de sens :
                on s'arrête à l'erreur plutôt que d'offrir un bouton inerte. */}
            {state.tourId && run && (
              <div className={classes.controlBar}>
                <div className={classes.controlGroup}>
                  <Typography className={classes.runInfo}>
                    {run.closedAt
                      ? t("liveScores.run.closed_since", {
                          time: formatTime(run.closedAt),
                        })
                      : t("liveScores.run.started_at", {
                          time: formatTime(run.startedAt),
                        })}
                    {" · "}
                    <span className={classes.runInfoStrong}>
                      {t("liveScores.run.tracked", {
                        count: run.sessionIds.length,
                      })}
                    </span>
                    {liveCount > 0
                      ? ` · ${t("liveScores.run.live", { count: liveCount })}`
                      : ""}
                  </Typography>
                  {displayControls}
                </div>

                <div className={classes.controlGroup}>
                  <Typography className={classes.runInfo}>
                    {t("liveScores.refresh.every", {
                      seconds: pollIntervalMs / 1000,
                    })}
                    {state.lastRefreshedAt
                      ? ` · ${t("liveScores.refresh.last_updated", {
                          time: formatTime(state.lastRefreshedAt),
                        })}`
                      : ""}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={onRefreshNow}
                    disabled={state.refreshing}
                    title={t("liveScores.refresh.refresh_now")}
                  >
                    {state.refreshing ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Refresh fontSize="small" />
                    )}
                  </IconButton>
                  {!run.closedAt && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Stop />}
                      className={classes.secondaryButton}
                      onClick={onCloseRun}
                    >
                      {t("liveScores.actions.close_run")}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrow />}
                    className={classes.primaryButton}
                    onClick={handleStartRun}
                  >
                    {t("liveScores.actions.new_run")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {state.tourId ? board : null}
        </div>
      </div>

      <Dialog open={confirmRestart} onClose={() => setConfirmRestart(false)}>
        <DialogTitle>{t("liveScores.confirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("liveScores.confirm.text")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestart(false)} color="default">
            {t("liveScores.confirm.cancel")}
          </Button>
          <Button
            onClick={() => {
              setConfirmRestart(false);
              onStartRun();
            }}
            color="primary"
            variant="contained"
          >
            {t("liveScores.confirm.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LiveScoresView;
